import { useCallback, useEffect, useRef } from 'react'

import { cn } from '@/utils/style'

interface MfaCodeInputProps {
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  hasError?: boolean
  autoFocus?: boolean
}

const CODE_LENGTH = 6

export function MfaCodeInput({
  value,
  onChange,
  onComplete,
  disabled = false,
  hasError = false,
  autoFocus = false,
}: MfaCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const digits = Array.from({ length: CODE_LENGTH }, (_, index) => value[index] || '')

  const focusInput = useCallback((index: number) => {
    inputRefs.current[index]?.focus()
  }, [])

  useEffect(() => {
    if (autoFocus) {
      focusInput(0)
    }
  }, [autoFocus, focusInput])

  useEffect(() => {
    if (value === '' && hasError) {
      focusInput(0)
    }
  }, [value, hasError, focusInput])

  const commitValue = (nextValue: string) => {
    onChange(nextValue)
    if (nextValue.length === CODE_LENGTH) {
      onComplete?.(nextValue)
    }
  }

  const handleChange = (index: number, nextChar: string) => {
    const sanitized = nextChar.replace(/\D/g, '').slice(-1)

    if (hasError) {
      commitValue(sanitized)
      focusInput(sanitized ? 1 : 0)
      return
    }

    const nextDigits = [...digits]
    nextDigits[index] = sanitized
    const nextValue = nextDigits.join('').slice(0, CODE_LENGTH)
    commitValue(nextValue)

    if (sanitized && index < CODE_LENGTH - 1) {
      focusInput(index + 1)
    }
  }

  const handleKeyDown = (index: number, key: string) => {
    if (key === 'Backspace' && !digits[index] && index > 0) {
      focusInput(index - 1)
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!pasted) {
      return
    }
    commitValue(pasted)
    focusInput(Math.min(pasted.length, CODE_LENGTH - 1))
  }

  return (
    <div className="flex gap-3">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          disabled={disabled}
          className={cn(
            'w-12 h-12 rounded-lg border text-center t-sb text-primary outline-none transition-colors',
            hasError ? 'border-error bg-error/5' : 'border-neutral-200 bg-neutral-50',
            'focus:border-btn-secondary',
            disabled && 'opacity-60 cursor-not-allowed',
          )}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event.key)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  )
}
